/**
 * Client-side WGS84 preview for GeoJSON/DXF import layer mapping.
 * Transforms S-JTSK (EPSG:5514) coordinates so Leaflet OSM tiles align.
 */

import type { ResolvedImportSourceCrs } from '@core/shared-bridge';
import { resolveImportSourceCrs } from '@core/shared-bridge';
import { sjtskToWgs84LngLat } from './sjtsk-coordinates.util';

export interface ImportPreviewFeature {
  type: 'Feature';
  geometry: {
    type: string;
    coordinates: unknown;
  };
  properties: Record<string, unknown>;
}

export interface ImportPreviewFeatureCollection {
  type: 'FeatureCollection';
  features: ImportPreviewFeature[];
}

/** Distinct overlay colors for CAD layers (cycle). */
export const IMPORT_LAYER_PREVIEW_COLORS = [
  '#2563eb', // blue
  '#dc2626', // red
  '#16a34a', // green
  '#ca8a04', // yellow
  '#9333ea', // purple
  '#ea580c', // orange
  '#0891b2', // cyan
  '#db2777', // pink
  '#4f46e5', // indigo
  '#65a30d', // lime
] as const;

/** Cap features painted on the mini-map (large CAD files stay importable without preview). */
export const IMPORT_PREVIEW_MAX_FEATURES = 800;

export function importLayerNameFromFeature(feature: ImportPreviewFeature | null | undefined): string {
  const raw = feature?.properties?.['Layer'];
  const text = raw == null ? '' : String(raw).trim();
  return text || 'UNNAMED_LAYER';
}

export function importLayerPreviewColor(layerIndex: number): string {
  const palette = IMPORT_LAYER_PREVIEW_COLORS;
  return palette[((layerIndex % palette.length) + palette.length) % palette.length];
}

export function resolveImportPreviewCrs(
  data: ImportPreviewFeatureCollection | null | undefined,
  selected: ResolvedImportSourceCrs | 'auto',
  detected: ResolvedImportSourceCrs | null,
): ResolvedImportSourceCrs {
  if (selected !== 'auto') return selected;
  if (detected) return detected;
  return resolveImportSourceCrs(data, 'auto');
}

function transformCoordPair(
  x: number,
  y: number,
  sourceCrs: ResolvedImportSourceCrs,
): [number, number] | null {
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if (sourceCrs === 'EPSG:4326') {
    // GeoJSON order: [lng, lat]
    if (Math.abs(x) > 180 || Math.abs(y) > 90) return null;
    return [x, y];
  }
  const wgs = sjtskToWgs84LngLat(x, y);
  return wgs ? [wgs.lng, wgs.lat] : null;
}

function transformCoordinates(coords: unknown, sourceCrs: ResolvedImportSourceCrs): unknown {
  if (!Array.isArray(coords) || coords.length === 0) return coords;
  if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
    const pair = transformCoordPair(Number(coords[0]), Number(coords[1]), sourceCrs);
    if (!pair) return null;
    // Preserve optional Z/M
    return coords.length > 2 ? [...pair, ...coords.slice(2)] : pair;
  }
  const out: unknown[] = [];
  for (const child of coords) {
    const next = transformCoordinates(child, sourceCrs);
    if (next == null) continue;
    out.push(next);
  }
  return out;
}

function geometryHasCoords(coords: unknown): boolean {
  if (!Array.isArray(coords) || coords.length === 0) return false;
  if (typeof coords[0] === 'number') return coords.length >= 2;
  return coords.some((c) => geometryHasCoords(c));
}

/**
 * Fair sample across CAD layers so one huge layer does not dominate the preview budget.
 */
export function selectImportPreviewFeatures(
  features: ImportPreviewFeature[],
  maxFeatures: number,
): { selected: ImportPreviewFeature[]; truncated: boolean } {
  if (!Array.isArray(features) || features.length === 0) {
    return { selected: [], truncated: false };
  }
  if (features.length <= maxFeatures) {
    return { selected: features, truncated: false };
  }

  const byLayer = new Map<string, ImportPreviewFeature[]>();
  for (const feature of features) {
    const name = importLayerNameFromFeature(feature);
    const bucket = byLayer.get(name);
    if (bucket) bucket.push(feature);
    else byLayer.set(name, [feature]);
  }

  const layerNames = Array.from(byLayer.keys());
  const perLayer = Math.max(1, Math.floor(maxFeatures / layerNames.length));
  const selected: ImportPreviewFeature[] = [];
  const leftovers: ImportPreviewFeature[] = [];

  for (const name of layerNames) {
    const bucket = byLayer.get(name) ?? [];
    selected.push(...bucket.slice(0, perLayer));
    if (bucket.length > perLayer) {
      leftovers.push(...bucket.slice(perLayer));
    }
  }

  for (const feature of leftovers) {
    if (selected.length >= maxFeatures) break;
    selected.push(feature);
  }

  return {
    selected: selected.slice(0, maxFeatures),
    truncated: true,
  };
}

/**
 * Build a WGS84 FeatureCollection for Leaflet preview.
 * Returns null when input is missing; empty features when transform yields nothing.
 */
export function buildImportPreviewFeatureCollection(
  source: ImportPreviewFeatureCollection | null | undefined,
  sourceCrs: ResolvedImportSourceCrs,
  options?: { maxFeatures?: number },
): { collection: ImportPreviewFeatureCollection; truncated: boolean } | null {
  if (!source || source.type !== 'FeatureCollection' || !Array.isArray(source.features)) {
    return null;
  }
  const max = options?.maxFeatures ?? IMPORT_PREVIEW_MAX_FEATURES;
  const { selected: slice, truncated } = selectImportPreviewFeatures(source.features, max);
  const features: ImportPreviewFeature[] = [];
  for (const feature of slice) {
    if (!feature?.geometry?.type || feature.geometry.coordinates == null) continue;
    const coordinates = transformCoordinates(feature.geometry.coordinates, sourceCrs);
    if (!geometryHasCoords(coordinates)) continue;
    features.push({
      type: 'Feature',
      geometry: {
        type: feature.geometry.type,
        coordinates,
      },
      properties: { ...(feature.properties ?? {}) },
    });
  }
  return {
    collection: { type: 'FeatureCollection', features },
    truncated,
  };
}

export function filterPreviewFeaturesByLayer(
  collection: ImportPreviewFeatureCollection,
  layerName: string,
): ImportPreviewFeatureCollection {
  const features = collection.features.filter(
    (f) => importLayerNameFromFeature(f) === layerName,
  );
  return { type: 'FeatureCollection', features };
}
